import { createTheme } from '@mui/material'

import { boxShadowFloat } from '@/_vars'
import { range } from '@/lib/array-utils'
import { brighterTurquoise, brightTurquoise, mainBlack, white } from '@/refactor/colors'
import { defaultFontFamily, FontFamily, pixelFontSize } from '@/refactor/fonts'
import { ggColors } from '@/refactor/ggColors'

import { DROPDOWN_Z_INDEX, POPOVER_INDEX } from './constants'

const defaultTransition = 'all 150ms ease-in-out'

const paperShadow = {
	boxShadow: boxShadowFloat,
}

const paperElevations = range(1, 24).reduce((all, c) => ({ ...all, ['elevation' + c]: paperShadow }), {})

const NARROW_GUTTER_SIZE = 15

export const theme = createTheme({
	typography: {
		fontFamily: defaultFontFamily,
		h1: {
			fontSize: '2rem', // 32px
			fontFamily: FontFamily.Comfortaa,
			lineHeight: 1.1875, // 38px
		},
		h2: {
			fontSize: '1.25rem', // 20px
			fontWeight: 700,
			fontFamily: FontFamily.Comfortaa,
			lineHeight: 1.4, // 28px
		},
		h3: {
			fontSize: '1.125rem', // 18px
			fontWeight: 600,
			lineHeight: 1.3888, // 25px
		},
		h4: {
			fontSize: '1rem', // 16px
			fontWeight: 700,
			lineHeight: 1.375, // 22px
		},
		h5: {
			fontSize: '1rem', // 16px
			lineHeight: 1.375, // 22px
		},
		h6: {
			fontSize: '0.875rem', // 14px
			lineHeight: 1.4285, // 20px
		},
		// subtitle1 => 16px
		// subtitle2 => 14px
		// body1 => 16px
		// body2 => 14px
		button: {
			// fontSize => 14px
			fontWeight: 700,
			textTransform: undefined, // MUI buttons are uppercase by default
		},
		// caption => 12px
		caption2: {
			fontSize: '0.625rem', // 10px
			fontWeight: 700,
			lineHeight: 1, // 10px
		},
		caption3: {
			fontSize: '0.5rem', // 8px
			fontWeight: 700,
			lineHeight: 1, // 8px
		},
		// overline => 12px
	},
	shape: {
		borderRadius: 5,
	},
	spacing: 5,
	breakpoints: {
		values: {
			xs: 0,
			sm: 420,
			md: 480,
			lg: 1024,
			lgx: 1024 + NARROW_GUTTER_SIZE * 2,
			xl: 1280,
		},
	},
	palette: {
		mode: 'light',
		text: {
			primary: ggColors.fontBlack,
			secondary: ggColors.secondaryBlack,
		},
		primary: {
			main: ggColors.brightTurquoise,
			contrastText: ggColors.white,
		},
		secondary: {
			main: ggColors.linkBlue,
			contrastText: ggColors.white,
		},
		success: {
			main: ggColors.successGreen,
			contrastText: ggColors.white,
		},
		error: {
			main: ggColors.brightRed,
			contrastText: ggColors.white,
		},
		warning: {
			main: ggColors.yellow,
			contrastText: ggColors.white,
		},
		info: {
			main: ggColors.linkBlue,
			contrastText: ggColors.white,
		},
	},
	zIndex: {
		// TODO: z-index layout system should be reworked
		modal: DROPDOWN_Z_INDEX,
	},
	components: {
		MuiFormControl: {
			styleOverrides: {
				root: {
					'&:focus': {
						outline: 'none',
					},
				},
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					color: ggColors.fontSecondary,
					fontSize: pixelFontSize(14),
					lineHeight: pixelFontSize(20),
					'&.Mui-focused': {
						color: ggColors.fontSecondary,
					},
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					zIndex: 1,
					pointerEvents: 'none',
					transform: 'translate(0, 24px) scale(1)',
				},
				animated: {
					transition: 'all 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
				},
				formControl: {
					top: 6,
					left: 20,
				},
				shrink: {
					transform: 'translate(0, 1.5px)',
					fontSize: pixelFontSize(12),
					left: 0,
					top: -5,
					pointerEvents: 'all',
				},
				asterisk: {
					color: ggColors.darkRed,
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				variant: 'filled',
				hiddenLabel: true,
			},
		},
		MuiAutocomplete: {
			styleOverrides: {
				option: {
					fontSize: '0.875rem', // 14px
				},
				listbox: (p) =>
					p.theme.unstable_sx({
						'& .MuiAutocomplete-option': {
							'&:not(:last-child)': {
								borderBottom: `solid 1px ${ggColors.backgroundGray}`,
							},
							paddingTop: 3,
							paddingBottom: 3,
						},
						'& .MuiAutocomplete-option.Mui-focused': {
							background: ggColors.hoverBlue,
						},

						'& .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused[aria-selected="true"]':
							{
								background: ggColors.hoverBlue,
							},
					}),
				groupLabel: {
					borderBottom: `1px solid ${ggColors.linkBlue}`,
					color: ggColors.linkBlue,
					fontWeight: 600,
					lineHeight: 2.5,
				},
				inputRoot: (p) =>
					p.theme.unstable_sx({
						'&.MuiFilledInput-root.MuiInputBase-hiddenLabel': {
							borderRadius: 1,

							'&:has(:disabled), &:has(.Mui-disabled)': {
								background: ggColors.disabledGray,
							},
							'&:hover:not(:has(:disabled)):not(:has(.Mui-disabled))': {
								background: ggColors.backgroundGray,
								boxShadow: boxShadowFloat,
							},
							'& .MuiAutocomplete-input': {
								padding: '14px 4px',

								'&:hover:not(:disabled):not(.Mui-disabled)': {
									boxShadow: 'none',
								},
								'&:focus': {
									background: ggColors.backgroundGray,
									boxShadow: 'none',
								},
								'&:invalid:hover, &:invalid:focus': {
									boxShadow: 'none',
								},
							},
						},
					}),
			},
		},
		MuiInputBase: {
			styleOverrides: {
				input: {
					borderRadius: '5px',
					fontFamily: defaultFontFamily,
					fontSize: pixelFontSize(14),
					lineHeight: pixelFontSize(20),
					background: ggColors.backgroundGray,
					padding: '15px 20px',
					transition: defaultTransition,
					border: '1px solid transparent',

					'&::placeholder': {
						color: ggColors.secondaryBlack,
						opacity: 1,
					},
					'&:hover:not(:disabled):not(.Mui-disabled)': {
						boxShadow: boxShadowFloat,
					},
					'&:focus': {
						background: ggColors.white,
						boxShadow: boxShadowFloat,
					},
					'&:invalid:hover, &:invalid:focus': {
						boxShadow: boxShadowFloat,
					},
					'&:disabled, &.Mui-disabled': {
						color: ggColors.fontSecondary,
						background: ggColors.disabledGray,
					},
				},
				inputMultiline: {
					padding: '15px 20px',
				},
				root: {
					'&.Mui-error input, &.Mui-error textarea': {
						borderColor: ggColors.darkRed,
					},
					'&::after': {
						display: 'none',
					},
					'&::before': {
						display: 'none',
					},
					'&.MuiInputBase-multiline': {
						padding: 0,
					},
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					fontSize: pixelFontSize(12),
					lineHeight: pixelFontSize(18),
					marginTop: 0,
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					borderRadius: '5px',
				},
				select: {
					transition: defaultTransition,
					border: '1px solid transparent',
					borderRadius: '5px',
					'&.Mui-disabled': {
						background: ggColors.disabledGray,
					},
				},
				icon: {
					right: 20,
					color: ggColors.fontBlack,
					fontSize: pixelFontSize(16),
					top: '50%',
					transform: 'translateY(-50%)',
					transition: defaultTransition,
				},
				iconOpen: {
					transform: 'translateY(-50%) rotate(-180deg)',
				},
			},
		},
		MuiMenu: {
			defaultProps: {
				disableScrollLock: true,
			},
			styleOverrides: {
				paper: {
					marginTop: 5,
					boxShadow: boxShadowFloat,
				},
				list: {
					paddingTop: 0,
					paddingBottom: 0,
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					color: ggColors.fontBlack,
					transition: defaultTransition,
					fontSize: pixelFontSize(14),
					paddingTop: 15,
					paddingBottom: 15,
					paddingLeft: 20,
					paddingRight: 20,
					display: 'block',
					'&&': {
						whiteSpace: 'normal',
					},
					'&$selected': {
						color: ggColors.brightTurquoise,
						background: 'transparent',
						'&:hover': {
							background: ggColors.hoverBlue,
						},
					},
					'&:hover': {
						background: ggColors.hoverBlue,
					},
				},
			},
		},
		MuiPopover: {
			styleOverrides: {
				root: {
					zIndex: POPOVER_INDEX,
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: paperShadow,
				...paperElevations,
			},
		},
		MuiFormControlLabel: {
			defaultProps: {
				slotProps: {
					typography: {
						variant: 'caption',
					},
				},
			},
			styleOverrides: {
				asterisk: (p) => ({
					color: ggColors.brightRed,
					fontSize: p.theme.typography.caption.fontSize,
				}),
			},
		},
		MuiStack: {
			defaultProps: {
				useFlexGap: true,
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					flexDirection: 'row',
					border: 'solid transparent',
					borderWidth: '4px 0',
					padding: '6px 10px',
					fontFamily: FontFamily.Comfortaa,
					textAlign: 'left',
					color: mainBlack,
					'&:hover': {
						color: brightTurquoise,
					},
					'&.Mui-selected': {
						borderBottomColor: brightTurquoise,
						transition: defaultTransition,
					},
					'&.Mui-focusVisible': {
						backgroundColor: brighterTurquoise,
					},
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					backgroundColor: white,
					variants: [
						{
							props: { orientation: 'vertical' },
							style: {
								padding: 0,
							},
						},
					],
				},
				list: {
					variants: [
						{
							props: { orientation: 'horizontal' },
							style: {
								justifyContent: 'center',
							},
						},
					],
				},
			},
		},
		MuiTooltip: {
			defaultProps: {
				arrow: true,
			},
		},
		MuiTypography: {
			defaultProps: {
				variantMapping: {
					body1: 'span',
					body2: 'span',
					caption: 'span',
					caption2: 'span',
					caption3: 'span',
					inherit: 'span',
				},
			},
		},
		MuiUseMediaQuery: {
			defaultProps: {
				noSsr: true,
			},
		},
	},
})
